// The one diff view (SCR-016, and the plan/terms comparisons elsewhere): resource changes and
// parameter changes as tables. Actions and replacements are written out in words, never colour
// alone; values are shown as data in monospace, since they may come from a customer account.
import type { components } from "../api/schema";

type ChangeSummary = components["schemas"]["InstallerChange"]["summary"];
export type ResourceChange = ChangeSummary["resources"][number];
export type ParameterChange = ChangeSummary["parameters"][number];

const ACTION_LABELS: Record<ResourceChange["action"], string> = {
  add: "Add",
  modify: "Modify",
  replace: "Replace",
  remove: "Remove",
};

export function ResourceDiff({ resources }: { resources: readonly ResourceChange[] }) {
  if (resources.length === 0) return <p class="diff-none">No resource changes.</p>;
  return (
    <div class="table-scroll">
      <table class="data-table diff-table">
        <caption>Resource changes</caption>
        <thead>
          <tr>
            <th scope="col">Action</th>
            <th scope="col">Type</th>
            <th scope="col">Logical id</th>
            <th scope="col">Replacement</th>
          </tr>
        </thead>
        <tbody>
          {resources.map((resource) => (
            <tr key={`${resource.logicalId}:${resource.action}`} class={`diff-${resource.action}`}>
              <td>{ACTION_LABELS[resource.action]}</td>
              <td>
                <code>{resource.type}</code>
              </td>
              <td>
                <code>{resource.logicalId}</code>
              </td>
              <td>{resource.replacement ? "Yes — resource is recreated" : "No"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Value({ value }: { value: string | null | undefined }) {
  return value === null || value === undefined ? <span class="diff-absent">(not set)</span> : <code>{value}</code>;
}

export function ParameterDiff({ parameters }: { parameters: readonly ParameterChange[] }) {
  if (parameters.length === 0) return <p class="diff-none">No parameter changes.</p>;
  return (
    <div class="table-scroll">
      <table class="data-table diff-table">
        <caption>Parameter changes</caption>
        <thead>
          <tr>
            <th scope="col">Parameter</th>
            <th scope="col">From</th>
            <th scope="col">To</th>
          </tr>
        </thead>
        <tbody>
          {parameters.map((parameter) => (
            <tr key={parameter.name}>
              <th scope="row">
                <code>{parameter.name}</code>
              </th>
              <td>
                <Value value={parameter.from} />
              </td>
              <td>
                <Value value={parameter.to} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
